// src/pages/ReportFormPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatRut, formatPhone } from '../utils/formatters';
import { reportService } from '../services/reportService';
import SignaturePad from 'signature_pad';
import imageCompression from 'browser-image-compression';
import { TrashIcon, PlusCircleIcon } from '@heroicons/react/24/outline';

const ReportFormPage = ({ visitId, setPage, setNotification }) => {
    const { user } = useAuth();
    const isEditMode = !!visitId;
    const [folio, setFolio] = useState(visitId || 'Por Asignar');
    
    // I. Datos del Cliente
    const [rut, setRut] = useState('');
    const [cliente, setCliente] = useState('');
    const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
    const [direccion, setDireccion] = useState('');
    const [encargado, setEncargado] = useState('');
    const [telefono, setTelefono] = useState('');
    const [email, setEmail] = useState('');

    // II. Información Visita
    const [horaInicio, setHoraInicio] = useState('');
    const [horaTermino, setHoraTermino] = useState('');
    const [tecnico, setTecnico] = useState('');
    const [tipoVisita, setTipoVisita] = useState('');

    // III. Detalle
    const [motivo, setMotivo] = useState('');
    const [diagnostico, setDiagnostico] = useState('');
    const [trabajos, setTrabajos] = useState('');

    // IV. Materiales (Con lógica de Stock)
    const [materiales, setMateriales] = useState([{ cantidad: 1, descripcion: '', codigo: '', productId: null }]);
    const [originalMateriales, setOriginalMateriales] = useState([]); 

    // V. Estado Final
    const [estadoFinal, setEstadoFinal] = useState('');
    const [recomendaciones, setRecomendaciones] = useState('');
    
    // VI. Firma y Conformidad (NUEVO)
    const [recepcionaNombre, setRecepcionaNombre] = useState('');
    const [recepcionaRut, setRecepcionaRut] = useState('');
    
    // Configuración dinámica
    const [visitTypeOptions, setVisitTypeOptions] = useState(['Mantención', 'Reparación', 'Instalación', 'Visita Técnica']);
    const [finalStatusOptions, setFinalStatusOptions] = useState(['100% Operativo', 'Operativo con Observaciones', 'Pendiente de Repuestos', 'Fuera de Servicio']); 

    // Gestión (VIII)
    const [billingType, setBillingType] = useState('Directa'); 

    const [photoPreviews, setPhotoPreviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Finalizar y Guardar');
    
    // Listas para autocompletar
    const [allClients, setAllClients] = useState([]);
    const [clientNameSearch, setClientNameSearch] = useState('');
    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [showClientSuggestions, setShowClientSuggestions] = useState(false);
    const clientSearchTimeout = useRef(null);
    const clientNameInputRef = useRef(null);

    // Buscador de Productos (Inventario)
    const [allProducts, setAllProducts] = useState([]);
    const [productSuggestions, setProductSuggestions] = useState({}); 

    const signaturePadRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);

    // --- CARGA INICIAL (USANDO SERVICIO) ---
    const fetchInitialData = useCallback(async () => {
        try {
            const data = await reportService.getInitialData();
            setAllClients(data.clients);
            setAllProducts(data.products);
            
            if (data.parameters) {
                if (data.parameters.visitTypes?.length > 0) setVisitTypeOptions(data.parameters.visitTypes);
                if (data.parameters.finalStatuses?.length > 0) setFinalStatusOptions(data.parameters.finalStatuses);
            }
        } catch (error) {
            console.error("Error fetching initial data:", error);
        }
    }, []);

    const loadVisitData = useCallback(async () => {
        if (!visitId) return;
        setLoading(true);
        try {
            const data = await reportService.getVisitById(visitId);
            if (data) {
                setRut(formatRut(data.clientId || ''));
                setCliente(data.clientName || '');
                setClientNameSearch(data.clientName || '');
                
                // Manejo seguro de fechas
                let dateStr = new Date().toISOString().split('T')[0];
                if (data.fecha) {
                    if (data.fecha.seconds) dateStr = new Date(data.fecha.seconds * 1000).toISOString().split('T')[0];
                    else if (data.fecha instanceof Date) dateStr = data.fecha.toISOString().split('T')[0];
                }
                setFecha(dateStr);

                setDireccion(data.direccion || '');
                setEncargado(data.encargado || '');
                setTelefono(formatPhone(data.telefono || ''));
                setEmail(data.email || '');
                setHoraInicio(data.horaInicio || '');
                setHoraTermino(data.horaTermino || '');
                setTecnico(data.tecnico || '');
                setTipoVisita(data.tipoVisita || '');
                setMotivo(data.motivo || '');
                setDiagnostico(data.diagnostico || '');
                setTrabajos(data.trabajos || '');
                
                const mats = data.materiales?.length > 0 ? data.materiales : [{ cantidad: 1, descripcion: '', codigo: '', productId: null }];
                setMateriales(mats);
                setOriginalMateriales(JSON.parse(JSON.stringify(mats))); 

                setEstadoFinal(data.estadoFinal || '');
                setRecomendaciones(data.recomendaciones || '');
                setRecepcionaNombre(data.recepcionaNombre || '');
                setRecepcionaRut(formatRut(data.recepcionaRut || ''));
                setBillingType(data.billingType || 'Directa');
                
                if (data.fotos) setPhotoPreviews(data.fotos.map(f => ({ src: f.url, name: f.name, file: null })));
                
                if (signaturePadRef.current && data.firmaDigital) {
                    setTimeout(() => signaturePadRef.current?.fromDataURL(data.firmaDigital), 100);
                }
            }
        } catch (error) {
            setNotification({ message: "Error al cargar el informe.", type: "error" });
        } finally {
            setLoading(false);
        }
    }, [visitId, setNotification]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            signaturePadRef.current = new SignaturePad(canvas, { backgroundColor: 'rgb(249, 250, 251)' });
            const resizeCanvas = () => {
                const ratio = Math.max(window.devicePixelRatio || 1, 1);
                canvas.width = canvas.offsetWidth * ratio;
                canvas.height = canvas.offsetHeight * ratio;
                canvas.getContext("2d").scale(ratio, ratio);
                // Si ya había firma, se borra al redimensionar si no la guardamos, 
                // pero en edición se recarga via loadVisitData.
                // signaturePadRef.current.clear(); 
            }
            window.addEventListener("resize", resizeCanvas);
            resizeCanvas();
            loadVisitData();
            return () => window.removeEventListener("resize", resizeCanvas);
        }
    }, [loadVisitData]);

    // --- Helpers Clientes ---
    const handleNameChange = (e) => {
        const name = e.target.value;
        setClientNameSearch(name);
        setCliente(name);
        if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current);
        if (name.length >= 3) {
            clientSearchTimeout.current = setTimeout(() => {
                const term = name.toLowerCase();
                const suggestions = allClients.filter(c => c.nombre.toLowerCase().includes(term)).slice(0, 10);
                setClientSuggestions(suggestions);
                setShowClientSuggestions(suggestions.length > 0);
            }, 100);
        } else {
            setShowClientSuggestions(false);
        }
    };

    const handleSelectClient = (c) => {
        setCliente(c.nombre);
        setClientNameSearch(c.nombre);
        setRut(formatRut(c.rut || c.id));
        setDireccion(c.direccion || '');
        setTelefono(formatPhone(c.telefono || ''));
        setEmail(c.email || '');
        setEncargado(c.encargado || '');
        setShowClientSuggestions(false);
    };

    const handleRutBlur = () => {
        const formatted = formatRut(rut);
        setRut(formatted);
        if (formatted.length < 8) return;
        const clean = formatted.replace(/[.-]/g, '');
        const match = allClients.find(c => c.id === clean || c.rut === clean);
        if (match) handleSelectClient(match);
    };

    // --- Helpers Materiales y Stock ---
    const handleMaterialChange = (index, field, value) => {
        const newMat = [...materiales];
        newMat[index][field] = value;
        setMateriales(newMat);

        // Lógica de Autocompletar Productos
        if (field === 'descripcion') {
            if (value.length >= 2) {
                const matches = allProducts.filter(p => p.name.toLowerCase().includes(value.toLowerCase()));
                setProductSuggestions(prev => ({ ...prev, [index]: matches }));
            } else {
                setProductSuggestions(prev => ({ ...prev, [index]: [] }));
            }
        }
    };

    const selectProductForMaterial = (index, product) => {
        const newMat = [...materiales];
        newMat[index] = {
            ...newMat[index],
            descripcion: product.name,
            codigo: product.id.substring(0, 6).toUpperCase(), 
            productId: product.id, 
            cantidad: 1
        };
        setMateriales(newMat);
        setProductSuggestions(prev => ({ ...prev, [index]: [] })); 
    };

    const addMaterialRow = () => setMateriales([...materiales, { cantidad: 1, descripcion: '', codigo: '', productId: null }]);
    const removeMaterialRow = (index) => {
        const newMat = materiales.filter((_, i) => i !== index);
        setMateriales(newMat.length ? newMat : [{ cantidad: 1, descripcion: '', codigo: '', productId: null }]);
    };

    // --- Helpers Fotos ---
    const handlePhotoChange = async (e) => {
        const files = Array.from(e.target.files);
        setNotification({ message: 'Procesando imágenes...', type: 'info' });
        try {
            const compressed = await Promise.all(files.map(f => imageCompression(f, { maxSizeMB: 0.8, maxWidthOrHeight: 1280 })));
            setPhotoPreviews(prev => [...prev, ...compressed.map(f => ({ src: URL.createObjectURL(f), name: f.name, file: f }))]);
            setNotification({ message: 'Imágenes listas.', type: 'success' });
        } catch (err) { console.error(err); }
    };

    // --- SUBMIT ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!diagnostico.trim() && !trabajos.trim()) { setNotification({ message: 'Falta diagnóstico o trabajos.', type: 'error' }); return; }
        
        setLoading(true);
        setLoadingMessage('Guardando...');
        
        // Preparar Datos para el Servicio
        const cleanRut = rut.replace(/[.-]/g, '');
        
        // Separar fotos nuevas de las existentes
        const newFiles = photoPreviews.filter(p => p.file).map(p => p.file);
        const existingInfos = photoPreviews.filter(p => !p.file).map(p => ({ url: p.src, name: p.name }));

        // Firma
        const signatureData = signaturePadRef.current?.isEmpty() ? null : signaturePadRef.current?.toDataURL();

        try {
            // EMPAQUETAR DATOS PLANOS
            const formData = {
                clientId: cleanRut,
                clientName: cliente,
                fecha: fecha, // String YYYY-MM-DD
                direccion, encargado, telefono, email, horaInicio, horaTermino, tecnico, tipoVisita,
                motivo, diagnostico, trabajos,
                estadoFinal, recomendaciones,
                recepcionaNombre, recepcionaRut: recepcionaRut.replace(/[.-]/g, ''),
                billingType
            };

            const materialContext = {
                current: materiales,
                original: originalMateriales
            };

            const photosContext = {
                newFiles,
                existingInfos
            };

            // LLAMAR AL SERVICIO (Él hace la magia: transacciones, stock, etc)
            await reportService.saveReport({
                visitId: isEditMode ? visitId : null,
                formData,
                materialContext,
                photosContext,
                signatureData
            });

            setNotification({ message: 'Guardado correctamente e Inventario actualizado.', type: 'success' });
            setTimeout(() => setPage('dashboard'), 1500);

        } catch (err) {
            console.error(err);
            setNotification({ message: 'Error al guardar.', type: 'error' });
        } finally { 
            setLoading(false); 
        }
    };

    if (loading && isEditMode) return <div className="p-8 text-center text-gray-600 dark:text-gray-400">Cargando...</div>;

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">{isEditMode ? `Editar Informe: ${visitId}` : `Nuevo Informe: ${folio}`}</h1>
                <button onClick={() => setPage('dashboard')} className="text-blue-600 dark:text-blue-400 hover:underline transition-colors">Volver</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 max-w-5xl mx-auto">
                
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                    <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">I. Datos del Cliente</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div><label className="block text-sm font-medium dark:text-gray-300">Fecha</label><input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" required/></div>
                        <div><label className="block text-sm font-medium dark:text-gray-300">RUT</label><input type="text" value={rut} onChange={e => setRut(formatRut(e.target.value))} onBlur={handleRutBlur} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" required/></div>
                        <div className="relative">
                            <label className="block text-sm font-medium dark:text-gray-300">Cliente</label>
                            <input type="text" value={clientNameSearch} onChange={handleNameChange} ref={clientNameInputRef} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" required/>
                            {showClientSuggestions && (
                                <ul className="absolute z-10 w-full bg-white dark:bg-gray-700 border dark:border-gray-600 mt-1 max-h-40 overflow-y-auto shadow-lg rounded">
                                    {clientSuggestions.map(c => <li key={c.id} onClick={() => handleSelectClient(c)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-sm dark:text-gray-100">{c.nombre}</li>)}
                                </ul>
                            )}
                        </div>
                        <div className="md:col-span-3"><label className="block text-sm font-medium dark:text-gray-300">Dirección</label><input type="text" value={direccion} onChange={e => setDireccion(e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"/></div>
                        <div><label className="block text-sm font-medium dark:text-gray-300">Encargado</label><input type="text" value={encargado} onChange={e => setEncargado(e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"/></div>
                        <div><label className="block text-sm font-medium dark:text-gray-300">Teléfono</label><input type="text" value={telefono} onChange={e => setTelefono(formatPhone(e.target.value))} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"/></div>
                        <div><label className="block text-sm font-medium dark:text-gray-300">Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"/></div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                    <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">II. Información Visita</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div><label className="block text-sm font-medium dark:text-gray-300">Hora Inicio</label><input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"/></div>
                        <div><label className="block text-sm font-medium dark:text-gray-300">Hora Término</label><input type="time" value={horaTermino} onChange={e => setHoraTermino(e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"/></div>
                        <div><label className="block text-sm font-medium dark:text-gray-300">Técnico</label><input type="text" value={tecnico} onChange={e => setTecnico(e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" required/></div>
                        <div className="md:col-span-3">
                            <label className="block text-sm font-medium dark:text-gray-300">Tipo Visita</label>
                            <select value={tipoVisita} onChange={e => setTipoVisita(e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                                <option value="">-- Seleccione --</option>
                                {visitTypeOptions.map(op => <option key={op} value={op}>{op}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                    <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">III. Detalle de la Intervención</h2>
                    <div className="space-y-4">
                        <div><label className="block text-sm font-medium dark:text-gray-300">Motivo / Problema</label><textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows="2" className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"/></div>
                        <div><label className="block text-sm font-medium dark:text-gray-300">Diagnóstico</label><textarea value={diagnostico} onChange={e => setDiagnostico(e.target.value)} rows="3" className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" required/></div>
                        <div><label className="block text-sm font-medium dark:text-gray-300">Trabajos Realizados</label><textarea value={trabajos} onChange={e => setTrabajos(e.target.value)} rows="3" className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"/></div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                    <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">IV. Materiales y Repuestos (Inventario)</h2>
                    <div className="space-y-2">
                        <div className="grid grid-cols-12 gap-2 font-bold text-sm text-gray-500 dark:text-gray-400">
                            <div className="col-span-2">Cant.</div>
                            <div className="col-span-6">Descripción (Buscar Producto)</div>
                            <div className="col-span-3">Código</div>
                            <div className="col-span-1"></div>
                        </div>
                        {materiales.map((mat, idx) => (
                            <div key={idx} className="relative grid grid-cols-12 gap-2 items-center">
                                <input type="number" value={mat.cantidad} onChange={e => handleMaterialChange(idx, 'cantidad', e.target.value)} className={`col-span-2 border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${mat.productId && allProducts.find(p => p.id === mat.productId)?.stock < mat.cantidad ? 'border-red-500 bg-red-50 text-red-700 font-bold' : ''}`} min="1"/>
                                
                                {/* BÚSQUEDA DE PRODUCTO */}
                                <div className="col-span-6 relative">
                                    <input 
                                        type="text" 
                                        value={mat.descripcion} 
                                        onChange={e => handleMaterialChange(idx, 'descripcion', e.target.value)} 
                                        className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" 
                                        placeholder="Escribe para buscar..."
                                        autoComplete="off"
                                    />
                                    {mat.productId && allProducts.find(p => p.id === mat.productId)?.stock < mat.cantidad && (
                                        <div className="text-[10px] text-red-600 dark:text-red-400 font-bold mt-1 animate-pulse">
                                            ⚠️ Stock insuficiente (Disponible: {allProducts.find(p => p.id === mat.productId)?.stock || 0})
                                        </div>
                                    )}
                                    {productSuggestions[idx] && productSuggestions[idx].length > 0 && (
                                        <ul className="absolute z-20 w-full bg-white dark:bg-gray-700 border dark:border-gray-600 mt-1 max-h-40 overflow-y-auto shadow-xl rounded left-0">
                                            {productSuggestions[idx].map(p => (
                                                <li 
                                                    key={p.id} 
                                                    onClick={() => selectProductForMaterial(idx, p)} 
                                                    className="p-2 hover:bg-blue-50 dark:hover:bg-gray-600 cursor-pointer text-sm border-b dark:border-gray-600 last:border-b-0"
                                                >
                                                    <div className="font-semibold dark:text-gray-100">{p.name}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">Stock: {p.stock || 0} - {p.unit}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <input type="text" value={mat.codigo} onChange={e => handleMaterialChange(idx, 'codigo', e.target.value)} className="col-span-3 border dark:border-gray-600 rounded p-2 bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-gray-300" readOnly={!!mat.productId} />
                                <button type="button" onClick={() => removeMaterialRow(idx)} className="col-span-1 text-red-500 hover:text-red-700 flex justify-center"><TrashIcon className="w-5 h-5"/></button>
                            </div>
                        ))}
                        <button type="button" onClick={addMaterialRow} className="text-blue-600 text-sm font-semibold hover:underline flex items-center mt-2"><PlusCircleIcon className="w-4 h-4 mr-1"/> Agregar Fila</button>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                    <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">V. Estado Final y Recomendaciones</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300">Estado Final del Equipo/Instalación</label>
                            <select 
                                value={estadoFinal} 
                                onChange={e => setEstadoFinal(e.target.value)} 
                                className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="">-- Seleccione Estado --</option>
                                {finalStatusOptions.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300">Recomendaciones Técnicas</label>
                            <textarea value={recomendaciones} onChange={e => setRecomendaciones(e.target.value)} rows="3" className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"/>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                    <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">VI. Firma y Conformidad</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300">Recibe (Nombre)</label>
                            <input type="text" value={recepcionaNombre} onChange={e => setRecepcionaNombre(e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Nombre completo" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300">Rut de Quien Recibe</label>
                            <input type="text" value={recepcionaRut} onChange={e => setRecepcionaRut(formatRut(e.target.value))} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Ej: 12.345.678-9" />
                        </div>
                    </div>
                    
                    <label className="block text-sm font-medium dark:text-gray-300 mb-2">Firma Digital</label>
                    <div className="border-2 border-dashed dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 h-48 relative overflow-hidden">
                        <canvas ref={canvasRef} className="w-full h-full block"/>
                    </div>
                    <button type="button" onClick={() => signaturePadRef.current.clear()} className="text-sm text-blue-600 dark:text-blue-400 hover:underline mt-2">Limpiar Firma</button>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                    <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">VII. Registro Fotográfico</h2>
                    <button type="button" onClick={() => fileInputRef.current.click()} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded font-semibold mb-4 border dark:border-green-800">📷 Adjuntar Fotos</button>
                    <input type="file" ref={fileInputRef} onChange={handlePhotoChange} multiple accept="image/*" className="hidden"/>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {photoPreviews.map((p, i) => (
                            <img key={i} src={p.src} alt="prev" className="w-full h-32 object-cover rounded border dark:border-gray-600"/>
                        ))}
                    </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-xl shadow-lg border border-blue-200 dark:border-blue-800/50">
                    <h2 className="text-xl font-semibold mb-4 text-blue-800 dark:text-blue-300">VIII. Gestión del Servicio</h2>
                    <div className="flex gap-6">
                        <label className="flex items-center cursor-pointer dark:text-gray-200">
                            <input type="radio" name="billing" value="Directa" checked={billingType === 'Directa'} onChange={e => setBillingType(e.target.value)} className="mr-2"/>
                            <span className="font-medium">Facturación Directa (Crear cobro)</span>
                        </label>
                        <label className="flex items-center cursor-pointer dark:text-gray-200">
                            <input type="radio" name="billing" value="Cotizacion" checked={billingType === 'Cotizacion'} onChange={e => setBillingType(e.target.value)} className="mr-2"/>
                            <span className="font-medium">Requiere Cotización (Solo informe)</span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-end pt-4">
                    <button type="submit" disabled={loading} className="bg-blue-600 dark:bg-blue-700 text-white px-8 py-3 rounded-lg font-bold shadow-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 transition-colors">
                        {loading ? loadingMessage : 'Guardar Informe'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReportFormPage;